import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyEmailToken } from '@services/emailVerification';
import { useAuth } from '@services/auth';
import { usePageActivityTracker } from '@services/activityTracker';

export default function VerifyEmailPage() {
	const [searchParams] = useSearchParams();
	usePageActivityTracker('verify_email');
	const { user, refreshUser } = useAuth();
	const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
	const [message, setMessage] = useState('Verifying your email...');
	const verifiedTokenRef = useRef<string | null>(null);

	useEffect(() => {
		const token = searchParams.get('token');
		if (!token) {
			setStatus('error');
			setMessage('Missing verification token.');
			return;
		}
		if (verifiedTokenRef.current === token) {
			return;
		}
		verifiedTokenRef.current = token;

		verifyEmailToken(token)
			.then((result) => {
				setStatus('success');
				setMessage(result.message || 'Email verified successfully.');
			})
			.catch((err: any) => {
				setStatus('error');
				setMessage(err?.message || 'Verification link is invalid or expired.');
			});
	}, [searchParams]);

	// After verify, auth context may still say unverified (stale from register). Sync when we have a user id.
	useEffect(() => {
		if (status !== 'success') return;
		let cancelled = false;
		(async () => {
			try {
				const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem('gardenhub:auth') : null;
				const stored = raw ? (JSON.parse(raw) as { id?: string } | null) : null;
				const id = user?.id ?? stored?.id;
				if (id && !cancelled) {
					await refreshUser(String(id));
				}
			} catch {
				/* ignore */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [status, user?.id, refreshUser]);

	return (
		<div className="container py-5">
			<div className="row justify-content-center">
				<div className="col-12 col-md-7 col-lg-5">
					<div className="card shadow-sm border-0">
						<div className="card-body p-4">
							<h1 className="h4 mb-3">Email Verification</h1>
							<div className={`alert ${status === 'success' ? 'alert-success' : status === 'error' ? 'alert-danger' : 'alert-info'} mb-3`}>
								{message}
							</div>
							<Link to="/login" className="btn btn-success w-100">
								Go to Login
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

